import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, RefreshCw } from "lucide-react";

// Super-admin control to show/hide (block/restore) embedded panels & widgets inside any room.
const RoomWidgetControls = ({ token }) => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Build a flat list of all rooms from the categories endpoint.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/rooms`, authHeader);
        const cats = res.data?.categories || {};
        const flat = [];
        const seen = new Set();
        const pushRoom = (r) => { if (r && !seen.has(r.room_id)) { seen.add(r.room_id); flat.push(r); } };
        Object.values(cats).forEach((cat) => {
          if (cat.subcategories) Object.values(cat.subcategories).forEach((sc) => (sc.rooms || []).forEach(pushRoom));
          else (cat.rooms || []).forEach(pushRoom);
        });
        flat.sort((a, b) => a.name.localeCompare(b.name));
        setRooms(flat);
        if (flat.length && !selectedRoom) setSelectedRoom(flat[0].room_id);
      } catch (e) {
        toast.error("Failed to load rooms");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadWidgets = useCallback(async (roomId) => {
    if (!roomId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/room-widgets/${roomId}`, authHeader);
      setCatalog(res.data?.catalog || []);
      setBlocked(res.data?.blocked || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load widgets");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (selectedRoom) loadWidgets(selectedRoom); }, [selectedRoom, loadWidgets]);

  const save = async (nextBlocked) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/room-widgets/${selectedRoom}`, { blocked: nextBlocked }, authHeader);
      setBlocked(nextBlocked);
      toast.success("Room widgets updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => {
    const next = blocked.includes(key) ? blocked.filter((k) => k !== key) : [...blocked, key];
    save(next);
  };

  return (
    <div className="p-3 space-y-3" data-testid="room-widgets-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-[#7B0099]">🧩 Room Widgets / Embedded Apps</span>
        <select
          className="retro-input text-xs flex-1 min-w-[180px]"
          value={selectedRoom}
          onChange={(e) => setSelectedRoom(e.target.value)}
          data-testid="widget-room-select"
        >
          {rooms.map((r) => <option key={r.room_id} value={r.room_id}>{r.name}</option>)}
        </select>
        <button onClick={() => loadWidgets(selectedRoom)} className="retro-button text-xs px-2 py-1" title="Reload">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
      <p className="text-xs text-gray-600">Toggle any embedded panel/widget on or off for the selected room. Hidden widgets are removed from that room for everyone.</p>

      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="widget-list">
          {catalog.map((w) => {
            const isBlocked = blocked.includes(w.key);
            return (
              <button
                key={w.key}
                onClick={() => toggle(w.key)}
                disabled={saving}
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded border text-left text-xs ${
                  isBlocked ? "bg-red-50 border-red-300 text-red-700" : "bg-green-50 border-green-300 text-green-800"
                }`}
                data-testid={`widget-toggle-${w.key}`}
              >
                <span className="truncate">{w.label}</span>
                {isBlocked
                  ? <span className="flex items-center gap-1 font-bold"><EyeOff className="w-3 h-3" /> Hidden</span>
                  : <span className="flex items-center gap-1 font-bold"><Eye className="w-3 h-3" /> Shown</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoomWidgetControls;
